import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: grid;
    grid-template-columns: 3fr 2fr;
    grid-gap: 50px;

    @media (max-width: 768px) {
      display: block;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;
const StyledPic = styled.div`
  position: relative;
  max-width: 300px;

  @media (max-width: 768px) {
    margin: 50px auto 0;
    width: 70%;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    background-color: var(--green);

    &:hover,
    &:focus {
      outline: 0;
      transform: translate(-4px, -4px);

      &:after {
        transform: translate(8px, 8px);
      }

      .img {
        filter: none;
        mix-blend-mode: normal;
      }
    }

    .img {
      position: relative;
      border-radius: var(--border-radius);
      mix-blend-mode: multiply;
      filter: grayscale(100%) contrast(1);
      transition: var(--transition);
    }

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      transition: var(--transition);
    }

    &:before {
      top: 0;
      left: 0;
      background-color: var(--navy);
      mix-blend-mode: screen;
    }

    &:after {
      border: 2px solid var(--green);
      top: 14px;
      left: 14px;
      z-index: -1;
    }
  }
`;

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = [
    'STM32G4',
    'ADC+DMA',
    'DDS',
    '模拟前端',
    '立创EDA',
    'Verilog',
    'Vivado',
    'Zynq',
    'Linux',
    'Docker',
  ];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              我是刘成，西南交通大学电子信息科学与技术专业 2025 级本科生。我真正感兴趣的事只有一件：
              <strong>把看不见的信号变成屏幕上的图像</strong>。大一一整年，几乎所有课余时间都投进了
              一台口袋大小的示波器。为了让 1.42MSPS 的采样链路稳定，模拟前端前后改了三版；为了在
              32KB RAM 里同时塞下双通道波形、菜单和测量，每一块缓冲区都按字节计算。它现在能测
              Vpp、频率，画李萨如图形，也能自己发出正弦波再测回来。
            </p>

            <p>
              这台机器教给我的，比它本身更有价值：示波器不是「采个 ADC 画条线」，而是模拟前端、
              时钟、触发、存储、显示、交互六个子系统相互妥协的结果。任何一个环节偷懒，屏幕上都会
              诚实地显示出来。
            </p>

            <p>
              现在我在做第二台，换到 Zynq-7010 上。目标是 10MHz 输入带宽、640×480 VGA 直出、拔掉
              电脑上电就能用。硬件已经跑通（SD 卡 Linux、JTAG、纯 PL bitstream），RTL 从零开始写。
            </p>

            <p>
              除此之外，我在阿里云 ECS 上维护一套自用的基础设施：跨设备文件同步、DERP 中继。
              它们不算作品，是工具——让我在任何设备上都能接着上一次的进度继续往下做。
            </p>

            <p>最近在接触：</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/me.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
